import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Machine } from 'xstate'
import { useMachine } from '@xstate/react'

import { Box, MotionBox, Heading, Text } from '@redriverpress/system'

const createFlashcardMachine = initialState =>
  Machine({
    id: 'flashcardMachine', // this will need to be unique if more than 1 FC shown at a time
    initial: initialState,
    states: {
      image: {
        on: {
          imageClicked: 'imageAndTitle',
        },
      },
      imageAndTitle: {
        on: {
          imageClicked: 'image',
          titleClicked: 'title',
        },
      },
      title: {
        on: {
          titleClicked: 'imageAndTitle',
        },
      },
    },
    on: {
      // Internal transitions
      // https://xstate.js.org/docs/guides/transitions.html#internal-transitions
      // These are used only to explicitly set the state based on the
      // global display option currently set in the application context.
      image: '.image',
      imageAndTitle: '.imageAndTitle',
      title: '.title',
    },
  })

const variants = {
  hide: {
    opacity: 0,
    scale: 0.75,
  },
  show: {
    opacity: 1,
    scale: 1,
  },
}

const transition = {
  type: 'spring',
  stiffness: 400,
  damping: 30,
  mass: 0.6,
}

const MotionHeading = motion(Heading)

const flashcardSizes = ['11rem', null, '20rem', '25rem', '22rem']

export default function Flashcard({ font, image, presentationMode, title }) {
  const [state, send] = useMachine(createFlashcardMachine(presentationMode))

  useEffect(() => {
    send(presentationMode)
  }, [presentationMode])

  const textSize =
    title.length > 26
      ? [3, null, state.matches('title') ? 5 : 4]
      : [4, null, state.matches('title') ? 8 : 6]

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
    >
      <AnimatePresence initial={false}>
        {!state.matches('title') && (
          <MotionBox
            layout
            key={image}
            variants={variants}
            transition={transition}
            initial='hide'
            animate='show'
            exit='hide'
            onClick={() => send('imageClicked')}
            role='button'
            display='inline-block'
            size={flashcardSizes}
            maxHeight='50vh' // prevent huge size on rotating device
            backgroundImage={`url(${image})`}
            backgroundRepeat='no-repeat'
            backgroundSize='contain'
            style={{ cursor: 'pointer' }}
            alt=''
          />
        )}
        {!state.matches('image') && (
          <MotionHeading
            layout
            key={title}
            as='h2'
            variants={variants}
            transition={transition}
            initial='hide'
            animate='show'
            exit='hide'
            onClick={() => send('titleClicked')}
            role='button'
            font={font}
            fontSize={textSize}
            fontWeight={font === 'standard' ? 800 : 700}
            textAlign='center'
            py={3}
            mb={0}
            style={{ cursor: 'pointer' }}
          >
            <Text
              as='span'
              fontSize={
                font === 'literacy' ? ['1.25em', null, null, '1.5em'] : '1em'
              }
            >
              {title}
            </Text>
          </MotionHeading>
        )}
      </AnimatePresence>
    </Box>
  )
}

Flashcard.defaultProps = {
  font: 'standard',
  presentationMode: 'image',
}

Flashcard.propTypes = {
  font: PropTypes.oneOf(['standard', 'literacy']),
  image: PropTypes.string.isRequired,
  presentationMode: PropTypes.oneOf(['image', 'title', 'imageAndTitle']),
  title: PropTypes.string.isRequired,
}
